/**
 * Connecting a Google account (GA4): send the agency user to Google's consent
 * page, then finish when Google sends them back to /oauth/google. The
 * one-time value kept in this tab before leaving must come back in `state`,
 * so a return that this browser did not start (someone else's code planted
 * in a link) is refused instead of linking a stranger's Google account.
 */

import { integrationsApi } from './integrations-api.ts';

const RETURN_PATH = '/oauth/google';
const PENDING_KEY = 'synanalytics:google-connect';
const NONCE_BYTES = 24;

interface Pending {
  organisationId: string;
  connectionId: string;
  nonce: string;
}

export const googleReturnUri = () => `${window.location.origin}${RETURN_PATH}`;

export function isGoogleReturn(): boolean {
  return window.location.pathname === RETURN_PATH;
}

function newNonce(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(NONCE_BYTES));
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/** Creates the connection when needed, then leaves for Google's consent page. */
export async function startGoogleConnect(
  organisationId: string,
  existingConnectionId?: string,
): Promise<void> {
  const connectionId =
    existingConnectionId ??
    (await integrationsApi.create(organisationId, 'ga4', 'Google Analytics')).id;
  const nonce = newNonce();
  const url = await integrationsApi.oauthUrl(
    organisationId,
    connectionId,
    googleReturnUri(),
    nonce,
  );
  const pending: Pending = { organisationId, connectionId, nonce };
  // Session storage: the value must survive the round trip to Google in this tab only.
  window.sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  window.location.assign(url);
}

/** Finishes a Google return; resolves with an error message, or null on success. */
export async function finishGoogleConnect(): Promise<string | null> {
  const params = new URLSearchParams(window.location.search);
  let pending: Pending | null = null;
  try {
    pending = JSON.parse(window.sessionStorage.getItem(PENDING_KEY) ?? 'null') as Pending | null;
  } finally {
    window.sessionStorage.removeItem(PENDING_KEY);
  }
  if (params.get('error')) return 'Google sign-in was cancelled, so nothing was connected.';
  const code = params.get('code');
  if (!pending || !code || params.get('state') !== `${pending.connectionId}.${pending.nonce}`)
    return 'This Google sign-in was not started from this tab, so it was refused. Start again from Data.';
  try {
    await integrationsApi.exchange(
      pending.organisationId,
      pending.connectionId,
      code,
      googleReturnUri(),
    );
    return null;
  } catch (problem) {
    return problem instanceof Error ? problem.message : 'Google could not be connected.';
  }
}
